/**
 * Every scale driver Taretto knows, and how a Bluetooth device finds its driver. The store and the UI go through
 * here and through the ScaleDriver interface; they never import a driver module. See README.md in this folder.
 *
 * Choosing a driver for a picked device:
 * 1. Name: the first driver whose matches() accepts the advertised name (no two drivers may claim one).
 * 2. No name match (Bluefy's "Show all devices", a blank or unexpected name): connect GATT and ask which drivers'
 *    primary services the device exposes. None: a clear error. The driver the user chose for this device before
 *    (remembered per device.id), if it is among them: that one. Exactly one, found by a service only that kind of
 *    scale uses (the Bookoo's 0FFE, the classic Acaia's 1820): that one. Otherwise ask the user "Which scale is this?"
 *    and remember the answer: several drivers fit (FFF0 is shared by several scales), or the one that fits was found
 *    only by generic UART services (FFE0, FFF0, Microchip's UART; see GENERIC_SERVICE_UUIDS), which plenty of devices
 *    that aren't scales expose too.
 * 3. Either way the driver's connect() then checks its primary service (getDriverService), so a name match is
 *    confirmed on the device itself.
 */
import { acaiaDriver } from './acaia';
import { bookooDriver } from './bookoo';
import { connectTimeout, describeBleError, exposedDriverServices, isBluetoothSupported, isGenericService, withArticle } from './bluetooth';
import { decentDriver } from './decent';
import { felicitaDriver } from './felicita';
import { simulatorDriver } from './simulator';
import type { BluetoothScaleDriverDescriptor, ScaleCapability, ScaleDriver, ScaleDriverDescriptor, ScaleDriverKind } from './types';

/** Real scales, in the order the picker lists their filters. A new driver adds its descriptor here. */
export const BLUETOOTH_DRIVERS: readonly BluetoothScaleDriverDescriptor[] = [bookooDriver, decentDriver, felicitaDriver, acaiaDriver];

/**
 * Every registered driver. The simulator is a dev-only entry: the static DEV check lets the production build drop
 * simulator.ts entirely (CI greps the bundle).
 */
export const SCALE_DRIVERS: readonly ScaleDriverDescriptor[] = import.meta.env.DEV ? [...BLUETOOTH_DRIVERS, simulatorDriver] : BLUETOOTH_DRIVERS;

const PROBE_TIMEOUT_MS = 15_000;

export function driverDescriptor(kind: ScaleDriverKind | null | undefined): ScaleDriverDescriptor | undefined {
  return kind ? SCALE_DRIVERS.find((d) => d.kind === kind) : undefined;
}

/** Bluetooth drivers reconnect after a drop; the simulator just ends. */
export function isBluetoothKind(kind: ScaleDriverKind): boolean {
  return driverDescriptor(kind)?.transport === 'bluetooth';
}

/** The driver that claims this advertised name, if any. */
export function driverForName(name: string | null | undefined, drivers: readonly BluetoothScaleDriverDescriptor[] = BLUETOOTH_DRIVERS): BluetoothScaleDriverDescriptor | undefined {
  return drivers.find((d) => d.matches(name));
}

/** "BOOKOO Themis Ultra / Mini" (and the others, comma separated), for copy that lists what is supported. */
export function supportedScalesText(drivers: readonly BluetoothScaleDriverDescriptor[] = BLUETOOTH_DRIVERS): string {
  return drivers.map(driverLabel).join(', ');
}

/** The label, with "(experimental)" for a driver nobody has confirmed on real hardware. */
export function driverLabel(d: Pick<ScaleDriverDescriptor, 'label' | 'experimental'>): string {
  return d.experimental ? `${d.label} (experimental)` : d.label;
}

/** A driver as the "Which scale is this?" prompt lists it. */
export interface BluetoothDriverChoice {
  kind: ScaleDriverKind;
  label: string;
  experimental: boolean;
}

export function driverChoice(d: BluetoothScaleDriverDescriptor): BluetoothDriverChoice {
  return { kind: d.kind, label: d.label, experimental: d.experimental === true };
}

/** The registered drivers' notes on a capability (settings hints), joined into one sentence string. */
export function capabilityNotes(capability: ScaleCapability, drivers: readonly ScaleDriverDescriptor[] = BLUETOOTH_DRIVERS): string {
  return drivers
    .filter((d) => d.capabilities.includes(capability))
    .map((d) => d.copy?.capabilityNotes?.[capability])
    .filter((n): n is string => !!n)
    .join(' ');
}

export interface ChosenDriver {
  descriptor: BluetoothScaleDriverDescriptor;
  /** The service probe left GATT connected (the driver's connect() reuses it); disconnect it when abandoning. */
  probed: boolean;
}

/**
 * "Which scale is this?": the device's name matched no driver, and either several drivers' services fit or the only
 * one that fits has generic services (a single candidate).
 */
export interface DriverQuestion {
  deviceName: string | null;
  candidates: readonly BluetoothScaleDriverDescriptor[];
}

/** Resolves with the user's choice among the candidates, or null when they cancel. */
export type AskDriver = (question: DriverQuestion) => Promise<BluetoothScaleDriverDescriptor | null>;

export interface ChooseDriverOptions {
  /** Without it, a device that several drivers could serve is an error. */
  ask?: AskDriver;
}

/** The store recognises "cancel" and stays quiet. */
export const DRIVER_CHOICE_CANCELLED = 'Scale choice cancelled.';

/**
 * Which driver talks to this picked device (see the file comment). Rejects with a message for the user when none
 * fits. `drivers` is what the picker offered.
 */
export async function chooseDriver(
  device: BluetoothDevice,
  drivers: readonly BluetoothScaleDriverDescriptor[] = BLUETOOTH_DRIVERS,
  options: ChooseDriverOptions = {},
): Promise<ChosenDriver> {
  const byName = driverForName(device.name, drivers);
  if (byName) return { descriptor: byName, probed: false };
  const label = device.name?.trim() ? `“${device.name.trim()}”` : 'That device';
  const unsupported = () => new Error(`${label} isn't a scale Taretto supports. Supported: ${supportedScalesText(drivers)}.`);
  const gatt = device.gatt;
  if (!gatt) throw unsupported();
  const timeout = connectTimeout(PROBE_TIMEOUT_MS);
  let exposed: BluetoothScaleDriverDescriptor[];
  /** Some fitting driver was found by a service of its own, not only by generic UART services. */
  let specific = false;
  try {
    const server = await Promise.race([gatt.connected ? Promise.resolve(gatt) : gatt.connect(), timeout.promise]);
    exposed = [];
    for (const d of drivers) {
      const services = await exposedDriverServices(server, d);
      if (services.length === 0) continue;
      exposed.push(d);
      if (services.some((uuid) => !isGenericService(uuid))) specific = true;
    }
  } catch (e) {
    gatt.disconnect();
    throw describeBleError(e);
  } finally {
    timeout.clear();
  }
  if (exposed.length === 0) {
    gatt.disconnect();
    throw unsupported();
  }
  const remembered = rememberedDriver(device, drivers);
  if (remembered && exposed.includes(remembered)) return { descriptor: remembered, probed: true };
  // A remembered choice the device no longer fits is stale.
  if (remembered) forgetDriverChoice(device);
  // One fitting driver decides only when the device has a service of that driver's own. FFE0, FFF0 or Microchip's
  // UART alone is any UART gadget: ask.
  if (exposed.length === 1 && specific) return { descriptor: exposed[0]!, probed: true };
  // Several drivers share these services (the FFF0 scales do), or the one is generic, and the name doesn't decide.
  if (!options.ask) {
    gatt.disconnect();
    throw new Error(
      exposed.length === 1
        ? `Taretto can't tell whether ${label} is ${withArticle(exposed[0]!.shortName)} scale (lots of Bluetooth devices use the same service).`
        : `Taretto can't tell which scale ${label} is (it could be ${withArticle(exposed.map((d) => d.shortName).join(' or '))}).`,
    );
  }
  let answer: BluetoothScaleDriverDescriptor | null;
  try {
    answer = await options.ask({ deviceName: device.name?.trim() || null, candidates: exposed });
  } catch (e) {
    gatt.disconnect();
    throw e;
  }
  if (!answer || !exposed.includes(answer)) {
    gatt.disconnect();
    throw new Error(DRIVER_CHOICE_CANCELLED);
  }
  rememberDriverChoice(device, answer.kind);
  return { descriptor: answer, probed: true };
}

// ---------------------------------------------------------------- remembered choices

/**
 * localStorage: the kind of the Bluetooth scale connected last. In Bluefy the picker filters to that scale alone
 * (see pickerRequest), so the usual scale isn't lost among every nearby device.
 */
export const LAST_SCALE_KIND_KEY = 'taretto.lastScaleKind';

/** The driver of the scale connected last, if it is still among `drivers`. */
export function lastUsedDriver(drivers: readonly BluetoothScaleDriverDescriptor[] = BLUETOOTH_DRIVERS): BluetoothScaleDriverDescriptor | undefined {
  try {
    const kind = localStorage.getItem(LAST_SCALE_KIND_KEY);
    return kind ? drivers.find((d) => d.kind === kind) : undefined;
  } catch {
    return undefined;
  }
}

export function rememberLastUsedDriver(kind: ScaleDriverKind): void {
  try {
    localStorage.setItem(LAST_SCALE_KIND_KEY, kind);
  } catch {
    // Private mode or storage blocked: Bluefy just lists every device next time.
  }
}

/** localStorage: { [device.id]: kind } for devices whose driver the user picked in "Which scale is this?". */
export const DRIVER_CHOICES_KEY = 'taretto.scaleDriverByDevice';

function readChoices(): Record<string, string> {
  try {
    const raw = localStorage.getItem(DRIVER_CHOICES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeChoices(choices: Record<string, string>): void {
  try {
    if (Object.keys(choices).length === 0) localStorage.removeItem(DRIVER_CHOICES_KEY);
    else localStorage.setItem(DRIVER_CHOICES_KEY, JSON.stringify(choices));
  } catch {
    // Private mode or storage blocked: the question just comes back next time.
  }
}

/** The driver the user chose for this device earlier, if it is still among `drivers`. */
export function rememberedDriver(
  device: Pick<BluetoothDevice, 'id'>,
  drivers: readonly BluetoothScaleDriverDescriptor[] = BLUETOOTH_DRIVERS,
): BluetoothScaleDriverDescriptor | undefined {
  if (!device.id) return undefined;
  const kind = readChoices()[device.id];
  return kind ? drivers.find((d) => d.kind === kind) : undefined;
}

export function rememberDriverChoice(device: Pick<BluetoothDevice, 'id'>, kind: ScaleDriverKind): void {
  if (!device.id) return;
  writeChoices({ ...readChoices(), [device.id]: kind });
}

export function forgetDriverChoice(device: Pick<BluetoothDevice, 'id'>): void {
  if (!device.id) return;
  const choices = readChoices();
  if (!(device.id in choices)) return;
  delete choices[device.id];
  writeChoices(choices);
}

/**
 * A driver bound to a scale this site was already granted, without showing the picker, or null. Needs Chrome's
 * getDevices() (behind a flag in 2026). It cannot tell whether the scale is switched on, so callers should connect
 * with a short timeout. Picks the first permitted device whose name a driver claims, or whose driver the user chose
 * in "Which scale is this?".
 */
export async function restoreScale(drivers: readonly BluetoothScaleDriverDescriptor[] = BLUETOOTH_DRIVERS): Promise<ScaleDriver | null> {
  if (!isBluetoothSupported() || typeof navigator.bluetooth.getDevices !== 'function') return null;
  let devices: BluetoothDevice[];
  try {
    devices = await navigator.bluetooth.getDevices();
  } catch {
    return null;
  }
  for (const device of devices) {
    const descriptor = driverForName(device.name, drivers) ?? rememberedDriver(device, drivers);
    if (descriptor) return descriptor.create(device);
  }
  return null;
}
